import { BODY_GROUND_OFFSET } from "../../lpc/body_placement"
import { Coordinate, Location, TileSize } from "../../models"

/** Logical coordinates are shared by parent and child; elevation is presentation only. */
export interface TwoStoreyHouse {
    buildingId: string
    left: number
    top: number
    width: number
    depth: number
    rise: number
    floor: number
    groundRoomId: string
    upperRoomId: string
    floorRoomIds?: string[]
    stairs?: { coordinate: Coordinate; landing: Location; label: "UP" | "DOWN" }[]
    entranceX: number
    /** Explicit cell masks support irregular footprints and different shapes on each storey. */
    floorCells: Coordinate[]
    /** Roof coverage is independent of the inset floor artwork and recessed upper storey. */
    roofCells?: Coordinate[]
    interiorCells: Coordinate[]
    wallCells: Coordinate[]
    stair: Coordinate
    landing: Location
}

export const cellKey = (at: Coordinate): string => `${at.x},${at.y}`
export const roofCellsForHouse = (house: TwoStoreyHouse): Coordinate[] => {
    if (house.roofCells) return house.roofCells
    // Earlier prototype camps only recorded the floor footprint. Include a southern eave
    // so the raised roof's bottom tiles reach the top of the front wall.
    const cells = new Map(house.floorCells.map(cell => [cellKey(cell), cell]))
    house.floorCells.forEach(({ x, y }) => cells.set(`${x},${y + 1}`, { x, y: y + 1 }))
    return [...cells.values()]
}
export const insideHouse = (house: TwoStoreyHouse, at: Coordinate): boolean => {
    const x = Math.floor(at.x / TileSize)
    const bodyY = Math.floor(at.y / TileSize)
    const feetY = Math.floor((at.y + BODY_GROUND_OFFSET) / TileSize)
    // Against the north wall the body centre enters the wall cell before the soles
    // leave the interior. Keep the cutaway while either reference remains inside.
    return house.interiorCells.some(cell => cell.x === x && (cell.y === bodyY || cell.y === feetY))
}
export const houseStairs = (house: TwoStoreyHouse) => house.stairs ?? (house.upperRoomId
    ? [{ coordinate: house.stair, landing: house.landing, label: house.floor === 0 ? "UP" as const : "DOWN" as const }] : [])
export const hasHouseRoof = (house?: TwoStoreyHouse): boolean => !!house &&
    (house.roofCells !== undefined ? house.roofCells.length > 0 : house.floor === 0 || !!house.floorRoomIds?.[house.floor + 1])
export const onHouseStair = (house: TwoStoreyHouse, at: Coordinate): boolean =>
    Math.floor(at.x / TileSize) === house.stair.x && Math.floor(at.y / TileSize) === house.stair.y
export const houseWallAt = (house: TwoStoreyHouse, x: number, y: number): boolean =>
    house.wallCells.some(cell => cell.x === x && cell.y === y)

/** A frame's scene visibility never determines its simulation or collision membership. */
export const companionFloorVisible = (active: TwoStoreyHouse, at: Coordinate): boolean =>
    active.floor > 0 || !insideHouse(active, at)
// Keep the room projection separate from tile placement. Builder sky tiles already
// include their roof edge artwork; lifting just this layer detaches it from its walls.
export const floorElevation = (house: TwoStoreyHouse, viewFloor = 0): number =>
    (Math.max(0, house.floor - 1) - (viewFloor > 0 && house.floor > viewFloor ? 1 : 0)) * house.rise * TileSize
export const companionFloorOffset = (active: TwoStoreyHouse, companion: TwoStoreyHouse): number =>
    floorElevation(active, active.floor) - floorElevation(companion, active.floor)
/** Roofs retain native builder coordinates, including in passive companion rooms. */
export const houseRoofOffset = (_house: TwoStoreyHouse, _viewFloor?: number): number => 0
